import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const CONNECTORS = [
  'ERP System', 'Supplier Portal', 'EDI', 'PO Database',
  'Invoice System', 'Email', 'Slack', 'Warehouse System',
]

const CONNECTOR_TABLES = {
  'ERP System': [
    'purchase_orders', 'inventory_levels', 'vendor_master', 'cost_centers',
    'general_ledger', 'material_master', 'goods_receipts', 'stock_transfers',
    'plant_data', 'valuation_records',
  ],
  'Supplier Portal': [
    'supplier_profiles', 'quote_requests', 'contract_terms', 'delivery_schedules',
    'compliance_docs', 'performance_scores', 'contact_directory', 'certifications',
  ],
  'EDI': [
    'edi_850_purchase_orders', 'edi_856_ship_notices', 'edi_810_invoices',
    'edi_855_acknowledgements', 'edi_997_func_acks', 'edi_940_warehouse_orders',
  ],
  'PO Database': [
    'purchase_orders', 'po_line_items', 'po_approvals', 'po_amendments',
    'blanket_orders', 'open_commitments', 'po_history', 'po_receipts',
  ],
  'Invoice System': [
    'invoices', 'invoice_line_items', 'payment_terms', 'credit_notes',
    'dispute_log', 'matched_receipts', 'vendor_payments', 'aging_report',
  ],
  'Email': [
    'inbox', 'sent_items', 'supplier_threads', 'escalation_mailbox',
    'order_confirmations', 'delivery_alerts',
  ],
  'Slack': [
    'procurement_channel', 'alerts_channel', 'supplier_updates', 'or_scheduling',
    'direct_messages', 'approvals_channel',
  ],
  'Warehouse System': [
    'inventory_locations', 'bin_contents', 'receiving_log', 'pick_lists',
    'expiry_tracking', 'consignment_stock', 'cycle_counts', 'transfer_orders',
  ],
}

const TRIGGER_OPTIONS = [
  { id: 'manual',    label: 'Manual',       desc: 'User-initiated on demand' },
  { id: 'schedule',  label: 'Schedule',     desc: 'Runs on a recurring cadence' },
  { id: 'event',     label: 'Event-based',  desc: 'Fires when a condition is met' },
  { id: 'threshold', label: 'Threshold',    desc: 'Activates when a value crosses a limit' },
]

const fieldLabel = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.09em',
  color: '#A39992',
  marginBottom: 6,
  display: 'block',
}

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: '9px 12px',
  color: '#FFFFFF',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  resize: 'none',
}

function AutocompleteInput({ value, onChange, options, placeholder, style }) {
  const [open, setOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const inputRef = useRef(null)
  const ref = useRef(null)

  const filtered = value.trim()
    ? options.filter(o => o.toLowerCase().includes(value.toLowerCase()))
    : options

  const updatePos = () => {
    if (!inputRef.current) return
    const r = inputRef.current.getBoundingClientRect()
    setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width })
  }

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref}>
      <input
        ref={inputRef}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => { updatePos(); setOpen(true) }}
        placeholder={placeholder}
        style={style}
        autoComplete="off"
      />
      <AnimatePresence>
        {open && filtered.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              zIndex: 9999,
              maxHeight: 180,
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            }}
          >
            {filtered.map(opt => (
              <div
                key={opt}
                onMouseDown={() => { onChange(opt); setOpen(false) }}
                style={{
                  padding: '7px 10px',
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.75)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {opt}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const chipStyle = {
  background: 'rgba(96,165,250,0.15)',
  border: '1px solid rgba(96,165,250,0.4)',
  borderRadius: 4,
  padding: '1px 8px',
  fontSize: 11,
  fontWeight: 600,
  color: '#60A5FA',
  display: 'inline-block',
  margin: '0 2px',
  userSelect: 'none',
  cursor: 'default',
}

function applyChipStyle(span) {
  Object.assign(span.style, chipStyle)
}

function createChip(name) {
  const span = document.createElement('span')
  span.setAttribute('contenteditable', 'false')
  span.setAttribute('data-connector', name)
  applyChipStyle(span)
  span.textContent = name
  return span
}

function getPlainText(el) {
  let out = ''
  el.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const connector = node.getAttribute && node.getAttribute('data-connector')
      if (connector) {
        out += '\\' + connector
      } else {
        out += getPlainText(node)
      }
    }
  })
  return out
}

function AgentGuideEditor({ initialText, onConnectorMentioned, onChange }) {
  const editorRef = useRef(null)
  const [picker, setPicker] = useState(null)
  const [query, setQuery] = useState('')
  const queryRangeRef = useRef(null)

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    el.innerHTML = ''
    const text = initialText ?? ''
    const re = /\\([A-Za-z][A-Za-z ]*)/g
    let lastIndex = 0
    let match
    while ((match = re.exec(text)) !== null) {
      let candidate = match[1]
      const found = CONNECTORS.find(c => candidate.startsWith(c))
      if (!found) continue
      if (match.index > lastIndex) {
        el.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      }
      el.appendChild(createChip(found))
      lastIndex = match.index + 1 + found.length
      re.lastIndex = lastIndex
    }
    if (lastIndex < text.length) {
      el.appendChild(document.createTextNode(text.slice(lastIndex)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!picker) return
    const handler = (e) => {
      if (!e.target.closest || !e.target.closest('[data-guide-picker]')) {
        closePicker()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker])

  const emitChange = () => {
    if (editorRef.current) onChange?.(getPlainText(editorRef.current))
  }

  const closePicker = () => {
    setPicker(null)
    setQuery('')
    queryRangeRef.current = null
  }

  const openPickerAtSelection = () => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return
    const rect = sel.getRangeAt(0).getBoundingClientRect()
    setPicker({ top: rect.bottom + 4, left: rect.left })
  }

  const handleKeyDown = (e) => {
    if (e.key === '\\') {
      e.preventDefault()
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        queryRangeRef.current = sel.getRangeAt(0).cloneRange()
      }
      setQuery('')
      openPickerAtSelection()
      return
    }
    if (picker) {
      if (e.key === 'Escape') {
        e.preventDefault()
        closePicker()
        return
      }
      if (e.key === 'Backspace') {
        if (query.length === 0) {
          closePicker()
        } else {
          setQuery(q => q.slice(0, -1))
        }
        e.preventDefault()
        return
      }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        setQuery(q => q + e.key)
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const filtered = CONNECTORS.filter(c => c.toLowerCase().includes(query.toLowerCase()))
        if (filtered[0]) insertChip(filtered[0])
        return
      }
    }
  }

  const insertChip = (name) => {
    const el = editorRef.current
    if (!el) return
    const sel = window.getSelection()
    let range = queryRangeRef.current
    if (range) {
      sel.removeAllRanges()
      sel.addRange(range)
    } else if (sel.rangeCount > 0) {
      range = sel.getRangeAt(0)
    } else {
      range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
    }
    range.deleteContents()
    const chip = createChip(name)
    range.insertNode(chip)

    const space = document.createTextNode(' ')
    if (chip.nextSibling) {
      chip.parentNode.insertBefore(space, chip.nextSibling)
    } else {
      chip.parentNode.appendChild(space)
    }

    const after = document.createRange()
    after.setStartAfter(space)
    after.collapse(true)
    sel.removeAllRanges()
    sel.addRange(after)

    el.focus()
    closePicker()
    onConnectorMentioned?.(name)
    emitChange()
  }

  const filtered = CONNECTORS.filter(c => c.toLowerCase().includes(query.toLowerCase()))

  return (
    <div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onKeyDown={handleKeyDown}
        style={{
          ...inputStyle,
          minHeight: 140,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      />
      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>Type \ to reference a connector</span>

      <AnimatePresence>
        {picker && (
          <motion.div
            data-guide-picker
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'fixed',
              top: picker.top,
              left: picker.left,
              minWidth: 180,
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              zIndex: 9999,
              maxHeight: 200,
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            }}
          >
            {query && (
              <div style={{ padding: '6px 10px', fontSize: 10, color: 'rgba(255,255,255,0.35)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                \{query}
              </div>
            )}
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 10px', fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>No matches</div>
            ) : filtered.map(opt => (
              <div
                key={opt}
                onMouseDown={(e) => { e.preventDefault(); insertChip(opt) }}
                style={{
                  padding: '7px 10px',
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.75)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.12)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {opt}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function CreateAgentPanel({ onClose, onSubmit, onAutonomyChange, initialData }) {
  const isEditing = Boolean(initialData?.name)
  const [name, setName]               = useState(initialData?.name ?? '')
  const [connectors, setConnectors]   = useState(initialData?.connectors ?? [])
  const [connectorConfig, setConnectorConfig] = useState(initialData?.connectorConfig ?? {})
  const [goal, setGoal]               = useState(initialData?.goal ?? '')
  const [guide, setGuide]             = useState(initialData?.guide ?? '')
  const [autonomy, setAutonomy]       = useState(initialData?.autonomy ?? 0)
  const handleAutonomyChange = (level) => {
    setAutonomy(level)
    if (name) onAutonomyChange?.(name, level)
  }
  const [trigger, setTrigger]         = useState(initialData?.trigger ?? 'manual')
  const [scheduleVal, setScheduleVal] = useState(initialData?.scheduleVal ?? '')
  const [eventVal, setEventVal]       = useState(initialData?.eventVal ?? '')
  const [thresholdVal, setThresholdVal] = useState(initialData?.thresholdVal ?? '')

  const toggleConnector = (c) =>
    setConnectors(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])

  const handleConnectorMentioned = (name) =>
    setConnectors(prev => prev.includes(name) ? prev : [...prev, name])

  const setConnectorField = (connector, field, value) =>
    setConnectorConfig(prev => ({ ...prev, [connector]: { ...prev[connector], [field]: value } }))

  const handleSubmit = () => {
    if (!name.trim()) return
    onSubmit?.({ name, connectors, goal, guide, trigger })
    onClose()
  }

  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 360, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      style={{ height: '100%', flexShrink: 0, overflow: 'hidden' }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        style={{
          width: 360,
          height: '100%',
          background: 'rgba(0,0,0,0.5)',
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div style={{ padding: '14px 16px 12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>{isEditing ? 'Agent Settings' : 'New Agent'}</span>
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.15, color: 'rgba(255,255,255,0.8)' }}
            whileTap={{ scale: 0.9 }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: 0, display: 'flex', alignItems: 'center' }}
          >
            <CloseIcon />
          </motion.button>
        </div>

        {/* Form */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Agent Name */}
          <div>
            <span style={fieldLabel}>Agent Name</span>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Contract Renewal Agent"
              style={inputStyle}
            />
          </div>

          {/* Agent Goal */}
          <div>
            <span style={fieldLabel}>Agent Goal</span>
            <textarea
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder="What should this agent achieve? e.g. Automatically renew expiring supplier contracts 30 days before the deadline."
              rows={3}
              style={inputStyle}
            />
          </div>

          {/* Agent Guide */}
          <div>
            <span style={fieldLabel}>Agent Guide</span>
            <AgentGuideEditor
              initialText={guide}
              onConnectorMentioned={handleConnectorMentioned}
              onChange={setGuide}
            />
          </div>

          {/* Connectors */}
          <div>
            <span style={fieldLabel}>Connectors</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CONNECTORS.map(c => {
                const active = connectors.includes(c)
                return (
                  <motion.button
                    key={c}
                    onClick={() => toggleConnector(c)}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      border: `1px solid ${active ? 'rgba(96,165,250,0.6)' : 'rgba(255,255,255,0.12)'}`,
                      background: active ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)',
                      color: active ? 'rgba(96,165,250,0.95)' : 'rgba(255,255,255,0.5)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {c}
                  </motion.button>
                )
              })}
            </div>

            {/* Data access config — appears when connectors are selected */}
            <AnimatePresence>
              {connectors.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  style={{ overflow: 'hidden', marginTop: 12 }}
                >
                  <div style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.15)', borderRadius: 8, padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'rgba(96,165,250,0.7)' }}>Data Access</span>
                    {connectors.map(c => {
                      const cfg = connectorConfig[c] ?? {}
                      return (
                        <div key={c} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em' }}>{c}</span>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                            {['Table', 'Row', 'Column'].map(field => (
                              <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{field}</span>
                                {field === 'Table' ? (
                                  <AutocompleteInput
                                    value={cfg.table ?? ''}
                                    onChange={val => setConnectorField(c, 'table', val)}
                                    options={CONNECTOR_TABLES[c] ?? []}
                                    placeholder="Any"
                                    style={{ ...inputStyle, fontSize: 11, padding: '6px 8px' }}
                                  />
                                ) : (
                                  <input
                                    value={cfg[field.toLowerCase()] ?? ''}
                                    onChange={e => setConnectorField(c, field.toLowerCase(), e.target.value)}
                                    placeholder="Any"
                                    style={{ ...inputStyle, fontSize: 11, padding: '6px 8px' }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Trigger */}
          <div>
            <span style={fieldLabel}>Trigger</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {TRIGGER_OPTIONS.map(opt => {
                const active = trigger === opt.id
                return (
                  <motion.div
                    key={opt.id}
                    onClick={() => setTrigger(opt.id)}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      border: `1px solid ${active ? 'rgba(96,165,250,0.45)' : 'rgba(255,255,255,0.07)'}`,
                      background: active ? 'rgba(96,165,250,0.1)' : 'rgba(255,255,255,0.03)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${active ? 'rgba(96,165,250,0.8)' : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(96,165,250,0.9)' }} />}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: active ? '#FFFFFF' : 'rgba(255,255,255,0.6)' }}>{opt.label}</span>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{opt.desc}</span>
                    </div>
                  </motion.div>
                )
              })}
            </div>

            {/* Trigger detail input */}
            <AnimatePresence mode="wait">
              {trigger === 'schedule' && (
                <motion.div key="schedule" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 10, overflow: 'hidden' }}>
                  <input value={scheduleVal} onChange={e => setScheduleVal(e.target.value)} placeholder="e.g. Every day at 8:00 AM" style={inputStyle} />
                </motion.div>
              )}
              {trigger === 'event' && (
                <motion.div key="event" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 10, overflow: 'hidden' }}>
                  <input value={eventVal} onChange={e => setEventVal(e.target.value)} placeholder="e.g. When a PO is unacknowledged for 24hrs" style={inputStyle} />
                </motion.div>
              )}
              {trigger === 'threshold' && (
                <motion.div key="threshold" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: 10, overflow: 'hidden' }}>
                  <input value={thresholdVal} onChange={e => setThresholdVal(e.target.value)} placeholder="e.g. When inventory drops below 20% of PAR" style={inputStyle} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Autonomy Level */}
          <div>
            <span style={fieldLabel}>Autonomy Level</span>
            <AutonomySlider value={autonomy} onChange={handleAutonomyChange} />
          </div>

        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <motion.button
            onClick={handleSubmit}
            whileHover={{ background: 'rgba(255,255,255,0.18)' }}
            whileTap={{ scale: 0.97 }}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            {isEditing ? 'Save Changes' : 'Create Agent'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  )
}

const AUTONOMY_LEVELS = [
  {
    label: 'Co-pilot',
    color: '#60A5FA',
    action: 'Approve / Adjust / Dismiss',
    desc: 'Shows the recommendation, the reasoning, and the options. Nothing has happened yet.',
  },
  {
    label: 'Semi-autonomous',
    color: '#A78BFA',
    action: 'Undo / Adjust',
    desc: 'The agent already acted within its limits and is telling you what it did and why.',
  },
  {
    label: 'Autonomous',
    color: '#4ADE80',
    action: 'View log',
    desc: 'A log entry. The agent handled it and would only surface this on an edge case.',
  },
]

function AutonomySlider({ value, onChange }) {
  const level = AUTONOMY_LEVELS[value]
  const pct = (value / 2) * 100

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '14px 14px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Track + thumb */}
      <div style={{ position: 'relative', paddingBottom: 20 }}>
        {/* Labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          {AUTONOMY_LEVELS.map((l, i) => (
            <span
              key={l.label}
              onClick={() => onChange(i)}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: i === value ? l.color : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                textAlign: i === 0 ? 'left' : i === 2 ? 'right' : 'center',
                flex: 1,
                transition: 'color 0.2s',
                userSelect: 'none',
              }}
            >
              {l.label}
            </span>
          ))}
        </div>

        {/* Track */}
        <div style={{ position: 'relative', height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)' }}>
          <motion.div
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 2, background: level.color }}
          />
          {/* Stop markers */}
          {[0, 1, 2].map(i => (
            <div
              key={i}
              onClick={() => onChange(i)}
              style={{
                position: 'absolute',
                left: `${(i / 2) * 100}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: i <= value ? level.color : 'rgba(255,255,255,0.15)',
                border: `2px solid ${i === value ? level.color : 'rgba(255,255,255,0.1)'}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
                zIndex: 1,
              }}
            />
          ))}
        </div>

        {/* Native input for drag support */}
        <input
          type="range"
          min={0}
          max={2}
          step={1}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            position: 'absolute',
            top: 18,
            left: 0,
            width: '100%',
            opacity: 0,
            cursor: 'pointer',
            height: 20,
            margin: 0,
          }}
        />
      </div>

      {/* Description card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={value}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
        >
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{level.desc}</span>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function CloseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
